import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useTranslation } from 'react-i18next';
import { faCircle, faSquare, faCopy, faFileImage, faFilePdf } from "@fortawesome/free-regular-svg-icons";
import EffectThemeTabButton from "../../effectTheme/effectThemeButtons";
import EffectThemeContainer from "../../effectTheme/effectThemeContainer";
import { faArrowPointer, faArrowRightArrowLeft, faArrowsRotate, faBold, faBorderNone, faBullseye,faCrop, faDiagramProject, faDownload, faEraser, faEyeDropper, faFillDrip, faFont, faHandPointer, faHexagonNodes, faImage, faItalic, faMagnifyingGlassMinus, faMagnifyingGlassPlus, faPaintBrush, faPowerOff, faRotateLeft, faRotateRight, faScissors, faShapes, faT, faTextHeight, faTrashCan, faUnderline, faUpload, faUpRightAndDownLeftFromCenter, faX } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { useEffectThemeStyles} from "../../effectTheme/effectThemeDropdown";
import EffectThemeInput from "../../effectTheme/effectThemeInput";
import { IconBorderWidth, IconDiamond, IconHexagon, IconOctagon, IconPentagon, IconRhombus, IconSingleLine, IconTrapeze, IconTriangle } from "../icons/Icons";
import ColorWheel from "../icons/ColorWheel";
import { getImageDimensions } from "../../../utils/images/editFunctions";
import downloadImageInFormat, { downloadScaleList, downloadScaleTypes, ExportFormat } from "../../../utils/images/imageConvertFormats";
import { SengiColors } from "../../../utils/SengiColors";
import { BasicImageToolKitProps, DrawingLayerToolbarProps, ImageLayerToolkitProps, LayerToolbarType, TextLayerToolbarProps, ToolbarPortalDropdownItemProps, ToolbarPortalDropdownProps, ToolkitItemsHeight, ToolkitLabelPosition, ToolkitLabelStyle } from "../../../types/imageStudio/toolbar";
import { HorizontalToolbarViewport } from "./ToolbarHorizontalViewport";
import { createPortal } from "react-dom";

const ToolbarPortalDropdown = ({
    button,
    children,
    menuWidth = 220,
    variant = "secondary",
    title = "",
    maxHeight,
    buttonClassname=''
}: ToolbarPortalDropdownProps) => {
    const [open, setOpen] = useState(false);
    const [menuStyleState, setMenuStyleState] = useState<React.CSSProperties>({});
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const menuRef = useRef<HTMLDivElement | null>(null);

    const { toggleStyle, menuStyle, isDark } = useEffectThemeStyles(variant);

    const updatePosition = () => {
        const button = buttonRef.current;
        if (!button) return;

        const rect = button.getBoundingClientRect();
        const padding = 8;
        const estimatedHeight = menuRef.current?.offsetHeight ?? 260;

        const left = Math.max(
            padding,
            Math.min(rect.left, window.innerWidth - menuWidth - padding)
        );

        const top = Math.max(
            padding,
            rect.top - estimatedHeight - 6
        );

        setMenuStyleState({
            position: "fixed",
            left,
            top,
            width: menuWidth,
            zIndex: 999999
        });
    };

    useEffect(() => {
        if (!open) return;

        updatePosition();

        const close = (event: MouseEvent) => {
            const target = event.target as Node;

            if (buttonRef.current?.contains(target)) return;
            if (menuRef.current?.contains(target)) return;

            setOpen(false);
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") setOpen(false);
        };

        window.addEventListener("mousedown", close);
        window.addEventListener("resize", updatePosition);
        window.addEventListener("scroll", updatePosition, true);
        window.addEventListener("keydown", onKeyDown);

        return () => {
            window.removeEventListener("mousedown", close);
            window.removeEventListener("resize", updatePosition);
            window.removeEventListener("scroll", updatePosition, true);
            window.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    return (
        <>
            <button
                ref={buttonRef}
                type="button"
                title={title}
                className={`btn p-1 rounded ${isDark ? "btn-dark" : "btn-light"} ${buttonClassname}`}
                style={{
                    ...toggleStyle,
                    height: ToolkitItemsHeight,
                    width: "auto"
                }}
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setOpen(prev => !prev);
                }}
            >
                {button}
            </button>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        className="show p-2"
                        style={{
                            ...menuStyle,
                            ...menuStyleState,
                            maxHeight: maxHeight ?? 'none',
                            overflowY: "auto",
                            overflowX: "hidden"
                        }}
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                    >
                        {children}
                    </div>,
                    document.body
                )}
        </>
    );
};

const ToolbarPortalDropdownItem = ({
    children,
    onClick,
    active = false,
    className = "",
    style
}: ToolbarPortalDropdownItemProps) => {
    const { itemBase, itemHoverBg, itemActiveBg } = useEffectThemeStyles();

    return (
        <button
            type="button"
            className={`btn border-0 w-100 text-start ${className}`}
            style={{
                ...itemBase, ...style,
                background: active ? itemActiveBg : "transparent"
            }}
            onClick={onClick}
            onMouseEnter={(e) => {
                e.currentTarget.style.background = itemHoverBg;
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.background = active ? itemActiveBg : "transparent";
            }}
        >
            {children}
        </button>
    );
};

export const BasicImageToolKit: React.FC<BasicImageToolKitProps> = ({
    activeMode,
    onChangeMode,
    hasSelectedLayer,
    hasMultipleSelectedLayers = false,
    selectedLayerType = null,
    selectedArea,
    canUndo,
    canRedo,
    downloadFormatList,
    upscaleCosts,
    colorPreviewList,
    isPickingTransparencyColor,
    onChangeColorPreviewList,
    canvasRef,
    image,
    windowSizeSmall=false,
    layers=[],
    onToggleTransparencyColorPicker,
    onZoomIn,
    onZoomOut,
    onDeleteSelectedLayers,
    onCreateDrawLayer,
    onCreateTextLayer,
    onCreateImageLayer,
    onCropSelectedLayer,
    onRotateSelectedLayer,
    onFlipHorizontalSelectedLayer,
    onFlipVerticalSelectedLayer,
    onReset,
    onUndo,
    onRedo,
    onToggleCopySelectedArea,
    onMergeSelectedWithPrevious,
    onMergeAllSelectedLayers,
    onToggleCropSelectedArea: onToggleExtractSelectedArea,
    onToggleDeleteSelectedArea,
    onToggleDuplicateSelectedLayer,
    onExpandSelectedLayerTransparent,
    selectedDrawingColor,
    selectedBrushSize,
    selectedBrushHardness,
    selectedBrushOpacity,
    selectedDrawingTool,
    isPickingDrawingColor,
    onChangeSelectedDrawingColor,
    onChangeSelectedBrushSize,
    onChangeSelectedBrushHardness,
    onChangeSelectedBrushOpacity,
    onChangeSelectedDrawingTool,
    onToggleDrawingColorPicker,
    fontTypesList = [],
    borderStyleList = [],
    borderWidthList = [],
    textSizesList = [],
    isPickingTextColor = false,
    isPickingBackgroundColor = false,
    isPickingBorderColor = false,
    textIsBold = false,
    textIsItalic = false,
    textIsUnderline = false,
    pickedTextColor = "#ffffff",
    pickedBorderColor = "#ffffff",
    pickedBackgroundColor = "#ffffff",
    textOpacity = 100,
    borderOpacity = 100,
    backgroundOpacity = 100,
    selectedTextSize = 16,
    selectedFontType = "Arial",
    selectedBorderWidth = 0,
    selectedBorderType = "none",
    onToggleTextColorPicker,
    onToggleBackgroundColorPicker,
    onToggleBorderColorPicker,
    onSelectTextColor,
    onSelectBorderColor,
    onSelectBackgroundColor,
    onChangeTextColorTransparency,
    onChangeBorderColorTransparency,
    onChangeBackgroundColorTransparency,
    onSelectTextSize,
    onSelectBorderWidth,
    onSelectBorderType,
    onSelectFontType,
    setTextStyleIsBold,
    setTextStyleIsItalic,
    setTextStyleIsUnderline,
    selectedImageLayerImage = null,
    selectedImageLayerOpacity = 100,
    selectedImageLayerBorderColor = "#ffffff",
    selectedImageLayerBorderOpacity = 100,
    selectedImageLayerBorderWidth = 0,
    selectedImageLayerBorderType = "none",
    selectedImageLayerBackgroundColor = "#ffffff",
    selectedImageLayerBackgroundOpacity = 100,
    imageBorderStyleList = [],
    imageBorderWidthList = [],
    isPickingImageLayerBackgroundColor = false,
    isPickingImageLayerBorderColor = false,
    onSelectImageLayerImage,
    onSelectImageLayerOpacity,
    onToggleImageLayerBackgroundColorPicker,
    onToggleImageLayerBorderColorPicker,
    onSelectImageLayerBorderColor,
    onSelectImageLayerBackgroundColor,
    onChangeImageLayerBorderOpacity,
    onChangeImageLayerBackgroundOpacity,
    onSelectImageLayerBorderWidth,
    onSelectImageLayerBorderType,
    canUndoSelectedLayer,
    canRedoSelectedLayer,
    onUndoSelectedLayer,
    onRedoSelectedLayer,
    onExportImage
}: BasicImageToolKitProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const [downloadRequest, setDownloadRequest] = useState<{
        format: ExportFormat;
        scale: downloadScaleTypes;
    } | null>(null);
    const [openLayerToolbar, setOpenLayerToolbar] = useState<LayerToolbarType>(null);
    const showLayerActions = useMemo(() => hasSelectedLayer, [hasSelectedLayer]);
    const selectionDisabled =
        hasMultipleSelectedLayers ||
        selectedLayerType === "text" 

    const cropDisabled =
        !hasSelectedLayer ||
        hasMultipleSelectedLayers ||
        selectedLayerType === "text";
    
    const transformDisabled =
        !hasSelectedLayer ||
        hasMultipleSelectedLayers;

    const deleteDisabled = !hasSelectedLayer;
    
    const mergeWithPreviousDisabled =
        !hasSelectedLayer || hasMultipleSelectedLayers;

    const mergeAllSelectedDisabled =
        !hasMultipleSelectedLayers;

    useEffect(() => {
        if (!downloadRequest) return;

        void onExportImage?.(downloadRequest.format, downloadRequest.scale);
        setDownloadRequest(null);
    }, [downloadRequest]);
    useEffect(() => {
        if (!hasSelectedLayer || hasMultipleSelectedLayers) {
            setOpenLayerToolbar(null);
            return;
        }

        if (selectedLayerType === "drawings") setOpenLayerToolbar("draw");
        else if (selectedLayerType === "text") setOpenLayerToolbar("text");
        else if (selectedLayerType === "image") setOpenLayerToolbar("image");
        else setOpenLayerToolbar(null);
    }, [selectedLayerType, hasSelectedLayer, hasMultipleSelectedLayers]);
    useEffect(() => {
        if (openLayerToolbar !== "draw" && selectedDrawingTool !== "") {
            onChangeSelectedDrawingTool("");
        }
    }, [openLayerToolbar, selectedDrawingTool, onChangeSelectedDrawingTool]);

    return (
        <div
            className="d-flex flex-column align-items-center position-relative"
            style={{
                width: "fit-content",
                maxWidth: "100%",
                minWidth: 0,
                zIndex: 20000,
            }}
        >
            
            {
                windowSizeSmall
                ?   <EffectThemeContainer
                        containerClass="toolbar-effect-container mb-1 p-0 mt-0 rounded d-inline-block overflow-hidden"
                        children={
                            <HorizontalToolbarViewport>
                                <div
                                    className="m-1 row align-items-start toolbar-main-row"
                                    style={{
                                        width: "max-content",
                                        minWidth: "max-content",
                                    }}
                                >
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.navigation')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${activeMode === "pointer" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('nav.pointer')}
                                                    onClick={() => onChangeMode(activeMode === "pointer" ? null : "pointer")}
                                                    children={<FontAwesomeIcon icon={faArrowPointer} size='sm' className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${activeMode === "hand" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('nav.hand')}
                                                    onClick={() => onChangeMode(activeMode === "hand" ? null : "hand")}
                                                    children={<FontAwesomeIcon icon={faHandPointer} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-secondary rounded"
                                                    title={t('nav.zoomOut')}
                                                    onClick={onZoomOut}
                                                    children={<FontAwesomeIcon icon={faMagnifyingGlassMinus} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-secondary rounded"
                                                    title={t('nav.zoomIn')}
                                                    onClick={onZoomIn}
                                                    children={<FontAwesomeIcon icon={faMagnifyingGlassPlus} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.area')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${activeMode === "select-oval" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('area.oval')}
                                                    onClick={() => onChangeMode(activeMode === "select-oval" ? null : "select-oval")}
                                                    children={<FontAwesomeIcon icon={faCircle} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                    disabled={selectionDisabled}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${activeMode === "select-rect" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('area.rectangle')}
                                                    onClick={() => onChangeMode(activeMode === "select-rect" ? null : "select-rect")}
                                                    children={<FontAwesomeIcon icon={faSquare} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                    disabled={selectionDisabled}
                                                />
                                            </div>
                                            <div className={`col-auto p-0 ps-1 ${selectedArea ? "" : "pe-1"}`}>
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${activeMode === "select-free" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('area.free')}
                                                    onClick={() => onChangeMode(activeMode === "select-free" ? null : "select-free")}
                                                    children={<FontAwesomeIcon icon={faBullseye} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                    disabled={selectionDisabled}
                                                />
                                            </div>
                                            {
                                                selectedArea
                                                ?   <>
                                                        <div className="col-auto p-0 ps-1">
                                                            <EffectThemeTabButton
                                                                className="btn p-1 btn-outline-danger rounded"
                                                                title={t('area.copy')}
                                                                onClick={onToggleCopySelectedArea}
                                                                children={<FontAwesomeIcon icon={faCopy} size="sm" className="p-1" />}
                                                                style={{height: ToolkitItemsHeight}}
                                                            />
                                                        </div>
                                                        <div className="col-auto p-0 ps-1">
                                                            <EffectThemeTabButton
                                                                className="btn p-1 btn-outline-danger rounded"
                                                                title={t('area.extract')}
                                                                onClick={onToggleExtractSelectedArea}
                                                                children={<FontAwesomeIcon icon={faCrop} size="sm" className="p-1" />}
                                                                style={{height: ToolkitItemsHeight}}
                                                            />
                                                        </div>
                                                        <div className="col-auto p-0 px-1">
                                                            <EffectThemeTabButton
                                                                className="btn p-1 btn-outline-danger rounded"
                                                                title={t('area.delete')}
                                                                onClick={onToggleDeleteSelectedArea}
                                                                children={<FontAwesomeIcon icon={faTrashCan} size="sm" className="p-1" />}
                                                                style={{height: ToolkitItemsHeight}}
                                                            />
                                                        </div>
                                                    </>
                                                :   <></>
                                            }
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.addLayer')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${openLayerToolbar === "draw" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('add.drawing')}
                                                    onClick={() => {
                                                        if (openLayerToolbar === "draw" && selectedLayerType === "drawings") {
                                                            setOpenLayerToolbar(null);
                                                            onChangeSelectedDrawingTool("");
                                                            return;
                                                        }

                                                        if (selectedLayerType === "drawings" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                            setOpenLayerToolbar("draw");
                                                            return;
                                                        }

                                                        onCreateDrawLayer();
                                                        setOpenLayerToolbar("draw");
                                                    }}
                                                    children={<FontAwesomeIcon icon={faPaintBrush} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>

                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${openLayerToolbar === "text" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('add.text')}
                                                    onClick={() => {
                                                        if (openLayerToolbar === "text" && selectedLayerType === "text") {
                                                            setOpenLayerToolbar(null);
                                                            return;
                                                        }

                                                        if (selectedLayerType === "text" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                            setOpenLayerToolbar("text");
                                                            return;
                                                        }

                                                        onCreateTextLayer();
                                                        setOpenLayerToolbar("text");
                                                    }}
                                                    children={<FontAwesomeIcon icon={faT} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>

                                            <div className="col-auto p-0 px-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${openLayerToolbar === "image" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('add.image')}
                                                    onClick={() => {
                                                        if (openLayerToolbar === "image" && selectedLayerType === "image") {
                                                            setOpenLayerToolbar(null);
                                                            return;
                                                        }

                                                        if (selectedLayerType === "image" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                            setOpenLayerToolbar("image");
                                                            return;
                                                        }

                                                        onCreateImageLayer();
                                                        setOpenLayerToolbar("image");
                                                    }}
                                                    children={<FontAwesomeIcon icon={faImage} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    {showLayerActions && (
                                        <div className="col-auto border-end border-secondary">
                                            <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                                <label className={ToolkitLabelStyle}>{t('label.layerFunctions')}</label>
                                            </div>
                                            <div className="row">
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.crop')}
                                                        onClick={onCropSelectedLayer}
                                                        disabled={cropDisabled}
                                                        children={<FontAwesomeIcon icon={faScissors} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        title={t('layerfn.expandTransparent')}
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        disabled={cropDisabled}
                                                        children={<FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        onClick={onExpandSelectedLayerTransparent}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.rotate')}
                                                        onClick={onRotateSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faArrowsRotate} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        disabled={transformDisabled}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.flipH')}
                                                        onClick={onFlipHorizontalSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faArrowRightArrowLeft} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        disabled={transformDisabled}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.flipV')}
                                                        onClick={onFlipVerticalSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faArrowRightArrowLeft} size="sm" rotation={90} className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        disabled={transformDisabled}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-danger rounded"
                                                        title={t('layerfn.duplicate')}
                                                        onClick={onToggleDuplicateSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faCopy} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.undo')}
                                                        onClick={onUndoSelectedLayer}
                                                        disabled={!canUndoSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faRotateLeft} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.redo')}
                                                        onClick={onRedoSelectedLayer}
                                                        disabled={!canRedoSelectedLayer}
                                                        children={<FontAwesomeIcon icon={faRotateRight} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-danger rounded"
                                                        title={t('layerfn.delete')}
                                                        onClick={onDeleteSelectedLayers}
                                                        disabled={deleteDisabled}
                                                        children={<FontAwesomeIcon icon={faTrashCan} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 ps-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.mergePrevVisible')}
                                                        onClick={onMergeSelectedWithPrevious}
                                                        children={<FontAwesomeIcon icon={faDiagramProject} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        disabled={mergeWithPreviousDisabled}
                                                    />
                                                </div>
                                                <div className="col-auto p-0 px-1">
                                                    <EffectThemeTabButton
                                                        className="btn p-1 btn-outline-secondary rounded"
                                                        title={t('layerfn.mergeAllVisible')}
                                                        onClick={onMergeAllSelectedLayers}
                                                        children={<FontAwesomeIcon icon={faHexagonNodes} size="sm" className="p-1" />}
                                                        style={{height: ToolkitItemsHeight}}
                                                        disabled={mergeAllSelectedDisabled}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                    <div className="col-auto">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.base')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-secondary rounded"
                                                    title={t('basefn.reset')}
                                                    onClick={onReset}
                                                    children={<FontAwesomeIcon icon={faPowerOff} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-secondary rounded"
                                                    title={t('basefn.undo')}
                                                    onClick={onUndo}
                                                    disabled={!canUndo}
                                                    children={<FontAwesomeIcon icon={faRotateLeft} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-secondary rounded"
                                                    title={t('basefn.redo')}
                                                    onClick={onRedo}
                                                    disabled={!canRedo}
                                                    children={<FontAwesomeIcon icon={faRotateRight} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 px-1">
                                                <ToolbarPortalDropdown
                                                    title={t('basefn.download')}
                                                    menuWidth={250}
                                                    button={<FontAwesomeIcon icon={faDownload} size="sm" className="p-1" />}
                                                >
                                                    <div className="row p-1 m-1 mb-0 pb-0">
                                                        <div className="col m-0 p-0 mb-1 text-small text-muted">
                                                            {t('label.format')}
                                                        </div>
                                                        <div className="col m-0 p-0 mb-1 text-small text-muted">
                                                            {t('menu.upscale')}
                                                            <p className="m-0 p-0 text-secondary">2x: {upscaleCosts?.upscale2x ?? '—'} {t('coins')}</p>
                                                            <p className="m-0 p-0 text-secondary">4x: {upscaleCosts?.upscale4x ?? '—'} {t('coins')}</p>
                                                        </div>
                                                    </div>
                                                    {downloadFormatList.map((item, index) => {
                                                        const format = item as ExportFormat;

                                                        return (
                                                            <div key={index} className="p-0 m-0">
                                                                <ToolbarPortalDropdownItem
                                                                    onClick={() => setDownloadRequest({ format, scale: "1x" })}
                                                                    className="p-1"
                                                                >
                                                                    <div className="row m-1">
                                                                        <div className="col-6 m-0 p-0">
                                                                            <span>
                                                                                <FontAwesomeIcon
                                                                                    className="me-2"
                                                                                    size="sm"
                                                                                    icon={item!== 'pdf' ? faFileImage : faFilePdf}
                                                                                />
                                                                                .{item}
                                                                            </span>
                                                                        </div>
                                                                        <div className="col-6 m-0 p-0 ps-2">
                                                                            {(format === "png" || format === "jpg") && (
                                                                                <div
                                                                                    className="btn-group btn-group-sm p-0"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                >
                                                                                    {downloadScaleList.map((scale) => (
                                                                                        <button
                                                                                            key={scale}
                                                                                            type="button"
                                                                                            className="btn btn-outline-secondary p-1"
                                                                                            onClick={() => setDownloadRequest({ format, scale })}
                                                                                        >
                                                                                            {scale}
                                                                                        </button>
                                                                                    ))}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    </div>
                                                                </ToolbarPortalDropdownItem>
                                                            </div>
                                                        );
                                                    })}
                                                </ToolbarPortalDropdown>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </HorizontalToolbarViewport>
                        }
                    />
            :   <EffectThemeContainer
                    containerClass="m-2 p-2 pb-0 mt-0 rounded d-inline-block"
                    children={
                        <>
                            <div className="mb-2 row g-1 align-items-center">
                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${activeMode === "pointer" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('nav.pointer')}
                                        onClick={() => onChangeMode(activeMode === "pointer" ? null : "pointer")}
                                        children={<FontAwesomeIcon icon={faArrowPointer} size='sm' className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${activeMode === "hand" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('nav.hand')}
                                        onClick={() => onChangeMode(activeMode === "hand" ? null : "hand")}
                                        children={<FontAwesomeIcon icon={faHandPointer} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className="btn p-1 btn-outline-secondary rounded"
                                        title={t('nav.zoomOut')}
                                        onClick={onZoomOut}
                                        children={<FontAwesomeIcon icon={faMagnifyingGlassMinus} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className="btn p-1 btn-outline-secondary rounded"
                                        title={t('nav.zoomIn')}
                                        onClick={onZoomIn}
                                        children={<FontAwesomeIcon icon={faMagnifyingGlassPlus} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>
                                <div className="col-auto p-0 ps-2 me-1">
                                    <label className="fw-bold">{t('label.area')}: </label>
                                </div>
                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${activeMode === "select-oval" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('area.oval')}
                                        onClick={() => onChangeMode(activeMode === "select-oval" ? null : "select-oval")}
                                        children={<FontAwesomeIcon icon={faCircle} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                        disabled={selectionDisabled}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${activeMode === "select-rect" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('area.rectangle')}
                                        onClick={() => onChangeMode(activeMode === "select-rect" ? null : "select-rect")}
                                        children={<FontAwesomeIcon icon={faSquare} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                        disabled={selectionDisabled}
                                    />
                                </div>

                                <div className={`col-auto ${selectedArea ? "" : "pe-2 border-end border-secondary"}`}>
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${activeMode === "select-free" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('area.free')}
                                        onClick={() => onChangeMode(activeMode === "select-free" ? null : "select-free")}
                                        children={<FontAwesomeIcon icon={faBullseye} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                        disabled={selectionDisabled}
                                    />
                                </div>

                                {
                                    selectedArea
                                    ?   <>
                                            <div className="col-auto">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-danger rounded"
                                                    title={t('area.copy')}
                                                    onClick={onToggleCopySelectedArea}
                                                    children={<FontAwesomeIcon icon={faCopy} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-danger rounded"
                                                    title={t('area.extract')}
                                                    onClick={onToggleExtractSelectedArea}
                                                    children={<FontAwesomeIcon icon={faCrop} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto pe-2 border-end border-secondary">
                                                <EffectThemeTabButton
                                                    className="btn p-1 btn-outline-danger rounded"
                                                    title={t('area.delete')}
                                                    onClick={onToggleDeleteSelectedArea}
                                                    children={<FontAwesomeIcon icon={faTrashCan} size="sm" className="p-1" />}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </>
                                    :   <></>
                                }
                                <div className="col-auto p-0 ps-2 me-1">
                                    <label className="fw-bold">{t('label.add')}: </label>
                                </div>
                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${openLayerToolbar === "draw" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('add.drawing')}
                                        onClick={() => {
                                            if (openLayerToolbar === "draw" && selectedLayerType === "drawings") {
                                                setOpenLayerToolbar(null);
                                                onChangeSelectedDrawingTool("");
                                                return;
                                            }

                                            if (selectedLayerType === "drawings" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                setOpenLayerToolbar("draw");
                                                return;
                                            }

                                            onCreateDrawLayer();
                                            setOpenLayerToolbar("draw");
                                        }}
                                        children={<FontAwesomeIcon icon={faPaintBrush} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${openLayerToolbar === "text" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('add.text')}
                                        onClick={() => {
                                            if (openLayerToolbar === "text" && selectedLayerType === "text") {
                                                setOpenLayerToolbar(null);
                                                return;
                                            }

                                            if (selectedLayerType === "text" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                setOpenLayerToolbar("text");
                                                return;
                                            }

                                            onCreateTextLayer();
                                            setOpenLayerToolbar("text");
                                        }}
                                        children={<FontAwesomeIcon icon={faT} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto pe-2 border-end border-secondary">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${openLayerToolbar === "image" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('add.image')}
                                        onClick={() => {
                                            if (openLayerToolbar === "image" && selectedLayerType === "image") {
                                                setOpenLayerToolbar(null);
                                                return;
                                            }

                                            if (selectedLayerType === "image" && hasSelectedLayer && !hasMultipleSelectedLayers) {
                                                setOpenLayerToolbar("image");
                                                return;
                                            }

                                            onCreateImageLayer();
                                            setOpenLayerToolbar("image");
                                        }}
                                        children={<FontAwesomeIcon icon={faImage} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                {showLayerActions && (
                                    <>
                                        <div className="col-auto p-0 ps-2 me-1">
                                            <label className="fw-bold">{t('label.layer')}: </label>
                                        </div>
                                        <div className="col-auto ps-2">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.crop')}
                                                onClick={onCropSelectedLayer}
                                                disabled={cropDisabled}
                                                children={<FontAwesomeIcon icon={faScissors} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                title={t('layerfn.expandTransparent')}
                                                className="btn p-1 btn-outline-secondary rounded"
                                                disabled={cropDisabled}
                                                children={<FontAwesomeIcon icon={faUpRightAndDownLeftFromCenter} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                onClick={onExpandSelectedLayerTransparent}
                                            />
                                        </div>
                                        

                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.rotate')}
                                                onClick={onRotateSelectedLayer}
                                                children={<FontAwesomeIcon icon={faArrowsRotate} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                disabled={transformDisabled}
                                            />
                                        </div>

                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.flipH')}
                                                onClick={onFlipHorizontalSelectedLayer}
                                                children={<FontAwesomeIcon icon={faArrowRightArrowLeft} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                disabled={transformDisabled}
                                            />
                                        </div>

                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.flipV')}
                                                onClick={onFlipVerticalSelectedLayer}
                                                children={<FontAwesomeIcon icon={faArrowRightArrowLeft} size="sm" rotation={90} className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                disabled={transformDisabled}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-danger rounded"
                                                title={t('layerfn.duplicate')}
                                                onClick={onToggleDuplicateSelectedLayer}
                                                children={<FontAwesomeIcon icon={faCopy} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.undo')}
                                                onClick={onUndoSelectedLayer}
                                                disabled={!canUndoSelectedLayer}
                                                children={<FontAwesomeIcon icon={faRotateLeft} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>

                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.redo')}
                                                onClick={onRedoSelectedLayer}
                                                disabled={!canRedoSelectedLayer}
                                                children={<FontAwesomeIcon icon={faRotateRight} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-danger rounded"
                                                title={t('layerfn.delete')}
                                                onClick={onDeleteSelectedLayers}
                                                disabled={deleteDisabled}
                                                children={<FontAwesomeIcon icon={faTrashCan} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.mergePrev')}
                                                onClick={onMergeSelectedWithPrevious}
                                                children={<FontAwesomeIcon icon={faDiagramProject} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                disabled={mergeWithPreviousDisabled}
                                            />
                                        </div>
                                        <div className="col-auto pe-2 border-end border-secondary">
                                            <EffectThemeTabButton
                                                className="btn p-1 btn-outline-secondary rounded"
                                                title={t('layerfn.mergeAll')}
                                                onClick={onMergeAllSelectedLayers}
                                                children={<FontAwesomeIcon icon={faHexagonNodes} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                                disabled={mergeAllSelectedDisabled}
                                            />
                                        </div>
                                    </>
                                )}
                                <div className="col-auto p-0 ps-2 me-1">
                                    <label className="fw-bold">{t('label.base')}: </label>
                                </div>
                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className="btn p-1 btn-outline-secondary rounded"
                                        title={t('basefn.reset')}
                                        onClick={onReset}
                                        children={<FontAwesomeIcon icon={faPowerOff} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className="btn p-1 btn-outline-secondary rounded"
                                        title={t('basefn.undo')}
                                        onClick={onUndo}
                                        disabled={!canUndo}
                                        children={<FontAwesomeIcon icon={faRotateLeft} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto pe-2 border-end border-secondary">
                                    <EffectThemeTabButton
                                        className="btn p-1 btn-outline-secondary rounded"
                                        title={t('basefn.redo')}
                                        onClick={onRedo}
                                        disabled={!canRedo}
                                        children={<FontAwesomeIcon icon={faRotateRight} size="sm" className="p-1" />}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>

                                <div className="col-auto ps-2">
                                    <ToolbarPortalDropdown
                                        title={t('basefn.download')}
                                        menuWidth={190}
                                        maxHeight={320}
                                        button={<FontAwesomeIcon icon={faDownload} size="sm" className="p-1" />}
                                    >
                                        <div className="row p-1 m-1 mb-0 pb-0">
                                            <div className="col m-0 p-0 mb-1 text-small text-muted">
                                                {t('label.format')}
                                            </div>
                                            <div className="col m-0 p-0 mb-1 text-small text-muted">
                                                {t('menu.upscale')}
                                                <p className="m-0 p-0 text-secondary">2x: {upscaleCosts ? upscaleCosts.upscale2x : 0} Coins</p>
                                                <p className="m-0 p-0 text-secondary">4x: {upscaleCosts ? upscaleCosts.upscale4x : 0} Coins</p>
                                            </div>
                                        </div>
                                        {downloadFormatList.map((item, index) => {
                                            const format = item as ExportFormat;

                                            return (
                                                <div key={index} className="p-0 m-0">
                                                    <ToolbarPortalDropdownItem
                                                        onClick={() => setDownloadRequest({ format, scale: "1x" })}
                                                        className="p-1"
                                                    >
                                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                                            <span>
                                                                <FontAwesomeIcon
                                                                    className="me-2"
                                                                    size="sm"
                                                                    icon={item!== 'pdf' ? faFileImage : faFilePdf}
                                                                />
                                                                .{format}
                                                            </span>

                                                            {(format === "png" || format === "jpg") && (
                                                                <div
                                                                    className="btn-group btn-group-sm p-0"
                                                                    onClick={(e) => e.stopPropagation()}
                                                                >
                                                                    {downloadScaleList.map((scale) => (
                                                                        <button
                                                                            key={scale}
                                                                            type="button"
                                                                            className="btn btn-outline-secondary p-1"
                                                                            onClick={() => {
                                                                                setDownloadRequest({ format, scale });
                                                                            }}
                                                                        >
                                                                            {scale}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </ToolbarPortalDropdownItem>
                                                </div>
                                            );
                                        })}
                                    </ToolbarPortalDropdown>
                                </div>
                            </div>
                        </>
                    }
                />
            }
            {openLayerToolbar === "draw" && (
                <DrawingLayerToolbar
                    colorPreviewList={colorPreviewList}
                    selectedColor={selectedDrawingColor}
                    selectedBrushSize={selectedBrushSize}
                    selectedHardness={selectedBrushHardness}
                    selectedOpacity={selectedBrushOpacity}
                    selectedDrawingTool={selectedDrawingTool}
                    isPickingDrawingColor={isPickingDrawingColor}
                    onChangeSelectedColor={onChangeSelectedDrawingColor}
                    onChangeSelectedBrushSize={onChangeSelectedBrushSize}
                    onChangeSelectedHardness={onChangeSelectedBrushHardness}
                    onChangeSelectedOpacity={onChangeSelectedBrushOpacity}
                    onChangeSelectedDrawingTool={onChangeSelectedDrawingTool}
                    onToggleDrawingColorPicker={onToggleDrawingColorPicker}
                    windowSizeSmall={windowSizeSmall}
                />
            )}
            {openLayerToolbar === "text" && (
                <TextLayerToolbar
                    fontTypesList={fontTypesList}
                    borderStyleList={borderStyleList}
                    borderWidthList={borderWidthList}
                    textSizesList={textSizesList}

                    isPickingTextColor={isPickingTextColor}
                    isPickingBackgroundColor={isPickingBackgroundColor}
                    isPickingBorderColor={isPickingBorderColor}

                    textIsBold={textIsBold}
                    textIsItalic={textIsItalic}
                    textIsUnderline={textIsUnderline}

                    pickedTextColor={pickedTextColor}
                    pickedBorderColor={pickedBorderColor}
                    pickedBackgroundColor={pickedBackgroundColor}

                    textOpacity={textOpacity}
                    borderOpacity={borderOpacity}
                    backgroundOpacity={backgroundOpacity}

                    selectedTextSize={selectedTextSize}
                    selectedFontType={selectedFontType}
                    selectedBorderWidth={selectedBorderWidth}
                    selectedBorderType={selectedBorderType}

                    onToggleTextColorPicker={onToggleTextColorPicker}
                    onToggleBackgroundColorPicker={onToggleBackgroundColorPicker}
                    onToggleBorderColorPicker={onToggleBorderColorPicker}

                    onSelectTextColor={onSelectTextColor}
                    onSelectBorderColor={onSelectBorderColor}
                    onSelectBackgroundColor={onSelectBackgroundColor}

                    onChangeTextColorTransparency={onChangeTextColorTransparency}
                    onChangeBorderColorTransparency={onChangeBorderColorTransparency}
                    onChangeBackgroundColorTransparency={onChangeBackgroundColorTransparency}

                    onSelectTextSize={onSelectTextSize}
                    onSelectBorderWidth={onSelectBorderWidth}
                    onSelectBorderType={onSelectBorderType}
                    onSelectFontType={onSelectFontType}

                    setTextStyleIsBold={setTextStyleIsBold}
                    setTextStyleIsItalic={setTextStyleIsItalic}
                    setTextStyleIsUnderline={setTextStyleIsUnderline}
                    windowSizeSmall={windowSizeSmall}
                />
            )}
            {openLayerToolbar === "image" && (
                <ImageLayerToolbar
                    image={selectedImageLayerImage}
                    imageOpacity={selectedImageLayerOpacity}
                    borderOpacity={selectedImageLayerBorderOpacity}
                    borderStyleList={imageBorderStyleList}
                    borderWidthList={imageBorderWidthList}
                    borderColor={selectedImageLayerBorderColor}
                    borderWidth={selectedImageLayerBorderWidth}
                    backgroundColor={selectedImageLayerBackgroundColor}
                    backgroundOpacity={selectedImageLayerBackgroundOpacity}
                    isPickingBackgroundColor={isPickingImageLayerBackgroundColor}
                    isPickingBorderColor={isPickingImageLayerBorderColor}
                    pickedBorderColor={selectedImageLayerBorderColor}
                    pickedBackgroundColor={selectedImageLayerBackgroundColor}
                    borderStyle={selectedImageLayerBorderType}
                    onChangeImage={onSelectImageLayerImage}
                    onChangeImageOpacity={onSelectImageLayerOpacity}
                    onToggleBackgroundColorPicker={onToggleImageLayerBackgroundColorPicker}
                    onToggleBorderColorPicker={onToggleImageLayerBorderColorPicker}
                    onSelectBorderColor={onSelectImageLayerBorderColor}
                    onSelectBackgroundColor={onSelectImageLayerBackgroundColor}
                    onChangeBorderColorTransparency={onChangeImageLayerBorderOpacity}
                    onChangeBackgroundColorTransparency={onChangeImageLayerBackgroundOpacity}
                    onSelectBorderWidth={onSelectImageLayerBorderWidth}
                    onSelectBorderType={onSelectImageLayerBorderType}
                    windowSizeSmall={windowSizeSmall}
                />
            )}
        </div>
    );
};

//TODO: DRAWING
/**
 * Color vorschläge generieren lassen
 * Farben passend zum bild vorschlagen lassen und liste neu setzen (colorPreviewList)
 * Härte anpassen
 */

export const DrawingLayerToolbar = ({
    colorPreviewList,
    selectedColor,
    selectedBrushSize,
    selectedHardness,
    selectedOpacity,
    selectedDrawingTool,
    isPickingDrawingColor = false,
    windowSizeSmall=false,
    onChangeSelectedColor,
    onChangeSelectedBrushSize,
    onChangeSelectedHardness,
    onChangeSelectedOpacity,
    onChangeSelectedDrawingTool,
    onToggleDrawingColorPicker,
}: DrawingLayerToolbarProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const brushSizesList = [1, 3, 5, 10, 20, 30, 50];

    const shapeList: { icon: JSX.Element; title: string; tool: string }[] = [
        {icon: <IconSingleLine/>, title:'Line', tool:'shape-line'},
        { icon: <FontAwesomeIcon icon={faCircle} />, title: "Circle", tool: "shape-circle" },
        { icon: <IconTriangle />, title: "Triangle", tool: "shape-triangle" },
        { icon: <IconTrapeze />, title: "Trapeze", tool: "shape-trapeze" },
        { icon: <IconRhombus />, title: "Rhombus", tool: "shape-rhombus" },
        { icon: <FontAwesomeIcon icon={faSquare} />, title: "Square", tool: "shape-square" },
        { icon: <IconPentagon />, title: "Pentagon", tool: "shape-pentagon" },
        { icon: <IconHexagon />, title: "Hexagon", tool: "shape-hexagon" },
        { icon: <IconOctagon />, title: "Octagon", tool: "shape-octagon" },
        { icon: <IconDiamond />, title: "Diamond", tool: "shape-diamond" },
    ];
        const hexToRgba = (hex: string, alpha: number) => {
        const clean = hex.replace("#", "");
        const full = clean.length === 3
            ? clean.split("").map((c) => c + c).join("")
            : clean;

        const r = parseInt(full.slice(0, 2), 16);
        const g = parseInt(full.slice(2, 4), 16);
        const b = parseInt(full.slice(4, 6), 16);

        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    const selectedColorWithOpacity = hexToRgba(
        selectedColor,
        Math.max(0, Math.min(1, selectedOpacity / 100))
    );

    return(
        <>
            {
                windowSizeSmall
                ?   <EffectThemeContainer
                        containerClass="toolbar-effect-container mb-1 p-0 mt-0 rounded d-inline-block overflow-hidden"
                        children={
                            <HorizontalToolbarViewport>
                                <div
                                    className="m-1 row align-items-start toolbar-main-row"
                                    style={{
                                        width: "max-content",
                                        minWidth: "max-content",
                                    }}
                                >
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.colors')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary me-1">
                                                <div
                                                    className="rounded-circle d-flex align-items-center justify-content-center"
                                                    style={{
                                                        width: "31px",
                                                        height: "31px",
                                                        backgroundColor: SengiColors.sng_lavendel,
                                                    }}
                                                >
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "25px",
                                                            height: "25px",
                                                            backgroundColor: '#ffff',
                                                        }}
                                                    >
                                                        <EffectThemeTabButton
                                                            className="btn p-0 rounded-circle border border-2 border-secondary"
                                                            title={t('color.selectedDrawing')}
                                                            onClick={() => onChangeSelectedColor(selectedColor)}
                                                            style={{
                                                                width: "25px",
                                                                height: "25px",
                                                                backgroundColor: selectedColorWithOpacity,
                                                                color: selectedColorWithOpacity,
                                                                backgroundImage: "none"
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            {colorPreviewList.map((item, index) => {
                                                const isSelected = selectedColor?.toLowerCase() === item.toLowerCase();

                                                return (
                                                    <div className="col-auto p-0" key={`${item}-${index}`}>
                                                        <div
                                                            className="rounded-circle d-flex align-items-center justify-content-center"
                                                            style={{
                                                                width: "31px",
                                                                height: "31px",
                                                                backgroundColor: isSelected ? SengiColors.sng_lavendel : "transparent"
                                                            }}
                                                        >
                                                            <EffectThemeTabButton
                                                                className={`btn p-0 rounded-circle border ${isSelected ? "border-2 border-secondary" : "border-secondary"}`}
                                                                title={t('color.selectDrawing')}
                                                                onClick={() => onChangeSelectedColor(item)}
                                                                style={{
                                                                    width: "25px",
                                                                    height: "25px",
                                                                    backgroundColor: item,
                                                                    color: item,
                                                                    backgroundImage: "none"
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}

                                            <div className="col-auto p-0">
                                                <label
                                                    className="rounded-circle d-flex align-items-center justify-content-center m-0"
                                                    title={t('color.chooseCustomDrawing')}
                                                    style={{
                                                        width: "31px",
                                                        height: "31px",
                                                        backgroundColor: "transparent",
                                                        cursor: "pointer",
                                                        position: "relative"
                                                    }}
                                                >
                                                    <div
                                                        className="rounded-circle border border-secondary d-flex align-items-center justify-content-center"
                                                        style={{
                                                            width: "25px",
                                                            height: "25px",
                                                            background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)"
                                                        }}
                                                    >
                                                        <ColorWheel />
                                                    </div>
                                                    <input
                                                        type="color"
                                                        value={selectedColor}
                                                        onChange={(e) => onChangeSelectedColor(e.target.value)}
                                                        style={{
                                                            position: "absolute",
                                                            inset: 0,
                                                            opacity: 0,
                                                            cursor: "pointer"
                                                        }}
                                                    />
                                                </label>
                                            </div>

                                            <div className="col-auto p-0 pe-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${isPickingDrawingColor ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('color.pickFromImage')}
                                                    onClick={() => onToggleDrawingColorPicker?.()}
                                                    children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1" />}
                                                    style={{ height: ToolkitItemsHeight }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle} title={t('color.sizeTooltip')}>{t('label.sizePx')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeInput
                                                    type="number"
                                                    name="seed"
                                                    value={selectedBrushSize}
                                                    min={0}
                                                    max={250}
                                                    step={1}
                                                    onValueChange={(v) => onChangeSelectedBrushSize(Number(v))}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <ToolbarPortalDropdown
                                                    title={t('color.selectBrushSize')}
                                                    menuWidth={140}
                                                    button={<FontAwesomeIcon icon={faBullseye} size="sm" className="p-1" />}
                                                >
                                                    {brushSizesList.map((item, index) => (
                                                        <ToolbarPortalDropdownItem
                                                            key={index}
                                                            active={selectedBrushSize === item}
                                                            onClick={() => onChangeSelectedBrushSize(item)}
                                                            className="p-1 ps-2"
                                                        >
                                                            {item} px
                                                        </ToolbarPortalDropdownItem>
                                                    ))}
                                                </ToolbarPortalDropdown>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle} title={t('color.sizeTooltip')}>{t('label.tools')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${selectedDrawingTool === "brush" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('color.brush')}
                                                    onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "brush" ? "" : "brush")}
                                                    children={<FontAwesomeIcon icon={faPaintBrush} size="sm" className="p-1" />}
                                                    style={{ height: ToolkitItemsHeight }}
                                                />
                                            </div>

                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${selectedDrawingTool === "eraser" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('color.eraser')}
                                                    onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "eraser" ? "" : "eraser")}
                                                    children={<FontAwesomeIcon icon={faEraser} size="sm" className="p-1" />}
                                                    style={{ height: ToolkitItemsHeight }}
                                                />
                                            </div>

                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${selectedDrawingTool === "fill" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                    title={t('color.fill')}
                                                    onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "fill" ? "" : "fill")}
                                                    children={<FontAwesomeIcon icon={faFillDrip} size="sm" className="p-1" />}
                                                    style={{ height: ToolkitItemsHeight }}
                                                />
                                            </div>
                                            
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <ToolbarPortalDropdown
                                                    title={t('color.chooseShape')}
                                                    menuWidth={190}
                                                    button={<FontAwesomeIcon icon={faShapes} size="sm" className="p-1" />}
                                                >
                                                    {shapeList.map((item, index) => (
                                                        <ToolbarPortalDropdownItem
                                                            key={index}
                                                            active={selectedDrawingTool === item.tool}
                                                            onClick={() => onChangeSelectedDrawingTool(item.tool)}
                                                            className="p-1 ps-2"
                                                        >
                                                            <div className="row text-start align-items-center">
                                                                <div className="col-3">
                                                                    <div
                                                                        className="d-inline-flex align-items-center justify-content-start"
                                                                        style={{ width: "24px", height: "24px" }}
                                                                    >
                                                                        {item.icon}
                                                                    </div>
                                                                </div>
                                                                <div className="col d-flex align-items-center">
                                                                    {item.title}
                                                                </div>
                                                            </div>
                                                        </ToolbarPortalDropdownItem>
                                                    ))}
                                                </ToolbarPortalDropdown>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle} title={t('color.hardnessTooltip')}>{t('label.hardnessPct')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <EffectThemeInput
                                                    type="number"
                                                    name="seed"
                                                    value={selectedHardness}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    onValueChange={(v) => onChangeSelectedHardness(Number(v))}
                                                    className="p-1"
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto me-1">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle} title={t('color.opacityTooltip')}>{t('label.opacityPct')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <EffectThemeInput
                                                    type="number"
                                                    name="seed"
                                                    value={selectedOpacity}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    onValueChange={(v) => onChangeSelectedOpacity(Number(v))}
                                                    className="p-1"
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </HorizontalToolbarViewport>
                        }
                    />
                :   <EffectThemeContainer
                        containerClass="p-1 ps-2 rounded d-inline-block mb-1"
                        children={
                            <div className="row p-1 align-items-center g-1">
                                <div className="col-auto p-0 ps-1  pe-2 border-end border-secondary">
                                    <div
                                        className="rounded-circle d-flex align-items-center justify-content-center"
                                        style={{
                                            width: "31px",
                                            height: "31px",
                                            backgroundColor: SengiColors.sng_lavendel,
                                        }}
                                    >
                                        <div className="rounded-circle d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "25px",
                                                height: "25px",
                                                backgroundColor: '#ffff',
                                            }}
                                        >
                                            <EffectThemeTabButton
                                                className="btn p-0 rounded-circle border border-2 border-secondary"
                                                title={t('color.selectedDrawing')}
                                                onClick={() => onChangeSelectedColor(selectedColor)}
                                                style={{
                                                    width: "25px",
                                                    height: "25px",
                                                    backgroundColor: selectedColorWithOpacity,
                                                    color: selectedColorWithOpacity,
                                                    backgroundImage: "none"
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {colorPreviewList.map((item, index) => {
                                    const isSelected = selectedColor?.toLowerCase() === item.toLowerCase();

                                    return (
                                        <div className="col-auto p-0 ps-1" key={`${item}-${index}`}>
                                            <div
                                                className="rounded-circle d-flex align-items-center justify-content-center"
                                                style={{
                                                    width: "31px",
                                                    height: "31px",
                                                    backgroundColor: isSelected ? SengiColors.sng_lavendel : "transparent"
                                                }}
                                            >
                                                <EffectThemeTabButton
                                                    className={`btn p-0 rounded-circle border ${isSelected ? "border-2 border-secondary" : "border-secondary"}`}
                                                    title={t('color.selectDrawing')}
                                                    onClick={() => onChangeSelectedColor(item)}
                                                    style={{
                                                        width: "25px",
                                                        height: "25px",
                                                        backgroundColor: item,
                                                        color: item,
                                                        backgroundImage: "none"
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    );
                                })}

                                <div className="col-auto p-0 ps-1">
                                    <label
                                        className="rounded-circle d-flex align-items-center justify-content-center m-0"
                                        title={t('color.chooseCustomDrawing')}
                                        style={{
                                            width: "31px",
                                            height: "31px",
                                            backgroundColor: "transparent",
                                            cursor: "pointer",
                                            position: "relative"
                                        }}
                                    >
                                        <div
                                            className="rounded-circle border border-secondary d-flex align-items-center justify-content-center"
                                            style={{
                                                width: "25px",
                                                height: "25px",
                                                background: "conic-gradient(red, yellow, lime, aqua, blue, magenta, red)"
                                            }}
                                        >
                                            <ColorWheel />
                                        </div>
                                        <input
                                            type="color"
                                            value={selectedColor}
                                            onChange={(e) => onChangeSelectedColor(e.target.value)}
                                            style={{
                                                position: "absolute",
                                                inset: 0,
                                                opacity: 0,
                                                cursor: "pointer"
                                            }}
                                        />
                                    </label>
                                </div>

                                <div className="col-auto ps-1 pe-2 border-end border-secondary">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${isPickingDrawingColor ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('color.pickFromImage')}
                                        onClick={() => onToggleDrawingColorPicker?.()}
                                        children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1" />}
                                        style={{ height: ToolkitItemsHeight }}
                                    />
                                </div>
                                <div className="col-auto p-0 ps-2 me-1">
                                    <label className="fw-bold" title={t('color.sizeTooltip')}>{t('label.size')}</label>
                                </div>
                                <div className="col-auto p-0">
                                    <EffectThemeInput
                                        type="number"
                                        name="seed"
                                        value={selectedBrushSize}
                                        min={0}
                                        max={250}
                                        step={1}
                                        onValueChange={(v) => onChangeSelectedBrushSize(Number(v))}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>
                                <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary">
                                    <ToolbarPortalDropdown
                                        title={t('color.selectBrushSize')}
                                        menuWidth={140}
                                        button={<FontAwesomeIcon icon={faBullseye} size="sm" className="p-1" />}
                                    >
                                        {brushSizesList.map((item, index) => (
                                            <ToolbarPortalDropdownItem
                                                key={index}
                                                active={selectedBrushSize === item}
                                                onClick={() => onChangeSelectedBrushSize(item)}
                                                className="p-1 ps-2"
                                            >
                                                {item} px
                                            </ToolbarPortalDropdownItem>
                                        ))}
                                    </ToolbarPortalDropdown>
                                </div>
                                <div className="col-auto ps-2">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${selectedDrawingTool === "brush" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('color.brush')}
                                        onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "brush" ? "" : "brush")}
                                        children={<FontAwesomeIcon icon={faPaintBrush} size="sm" className="p-1" />}
                                        style={{ height: ToolkitItemsHeight }}
                                    />
                                </div>

                                <div className="col-auto">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${selectedDrawingTool === "eraser" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('color.eraser')}
                                        onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "eraser" ? "" : "eraser")}
                                        children={<FontAwesomeIcon icon={faEraser} size="sm" className="p-1" />}
                                        style={{ height: ToolkitItemsHeight }}
                                    />
                                </div>

                                <div className="col-auto pe-2 border-end border-secondary">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${selectedDrawingTool === "fill" ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                        title={t('color.fill')}
                                        onClick={() => onChangeSelectedDrawingTool(selectedDrawingTool === "fill" ? "" : "fill")}
                                        children={<FontAwesomeIcon icon={faFillDrip} size="sm" className="p-1" />}
                                        style={{ height: ToolkitItemsHeight }}
                                    />
                                </div>
                                
                                <div className="col-auto p-0 ps-2">
                                    <ToolbarPortalDropdown
                                        title={t('color.chooseShape')}
                                        menuWidth={190}
                                        button={<FontAwesomeIcon icon={faShapes} size="sm" className="p-1" />}
                                    >
                                        {shapeList.map((item, index) => (
                                            <ToolbarPortalDropdownItem
                                                key={index}
                                                active={selectedDrawingTool === item.tool}
                                                onClick={() => onChangeSelectedDrawingTool(item.tool)}
                                                className="p-1 ps-2"
                                            >
                                                <div className="row text-start align-items-center">
                                                    <div className="col-3">
                                                        <div
                                                            className="d-inline-flex align-items-center justify-content-start"
                                                            style={{ width: "24px", height: "24px" }}
                                                        >
                                                            {item.icon}
                                                        </div>
                                                    </div>
                                                    <div className="col d-flex align-items-center">
                                                        {item.title}
                                                    </div>
                                                </div>
                                            </ToolbarPortalDropdownItem>
                                        ))}
                                    </ToolbarPortalDropdown>
                                </div>
                                <div className="col-auto p-0 ps-2">
                                    <label className="fw-bold" title={t('color.hardnessTooltipPct')}>{t('label.hardness')}</label>
                                </div>
                                <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary">
                                    <EffectThemeInput
                                        type="number"
                                        name="seed"
                                        value={selectedHardness}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onValueChange={(v) => onChangeSelectedHardness(Number(v))}
                                        className="p-1"
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>
                                <div className="col-auto p-0 ps-2">
                                    <label className="fw-bold" title={t('color.opacityTooltipPct')}>{t('label.opacity')}</label>
                                </div>
                                <div className="col-auto p-0 ps-1 pe-2">
                                    <EffectThemeInput
                                        type="number"
                                        name="seed"
                                        value={selectedOpacity}
                                        min={0}
                                        max={100}
                                        step={1}
                                        onValueChange={(v) => onChangeSelectedOpacity(Number(v))}
                                        className="p-1"
                                        style={{height: ToolkitItemsHeight}}
                                    />
                                </div>
                            </div>
                            
                        }
                    />
            }
            
        </>
    );
};
/*TOOLS:*
 * Change Text Color
 * Change Text size
 * Change Font
 * Styling: Bold, italic, underline
 * Change Background color
 * Add border and change width
 * Change border line (solid, dashed points)
 * Change border color
 * If AI mode active: Transform Text into image type with description and choose from variations
*/
export const TextLayerToolbar = ({
    fontTypesList = [],
    borderStyleList = [],
    borderWidthList = [],
    textSizesList = [],
    isPickingBackgroundColor = false,
    isPickingBorderColor = false,
    isPickingTextColor = false,
    textIsBold = false,
    textIsItalic = false,
    textIsUnderline = false,
    pickedBackgroundColor = "#ffffff",
    pickedBorderColor = "#ffffff",
    pickedTextColor = "#ffffff",
    textOpacity = 100,
    borderOpacity = 100,
    backgroundOpacity = 100,
    selectedTextSize = 16,
    selectedFontType = "Arial",
    selectedBorderWidth = 1,
    selectedBorderType = "none",
    windowSizeSmall=false,
    onToggleBackgroundColorPicker,
    onToggleBorderColorPicker,
    onToggleTextColorPicker,
    onSelectBackgroundColor,
    onSelectBorderColor,
    onSelectBorderType,
    onSelectBorderWidth,
    onSelectFontType,
    onSelectTextColor,
    onSelectTextSize,
    setTextStyleIsBold,
    setTextStyleIsItalic,
    setTextStyleIsUnderline,
    onChangeBackgroundColorTransparency,
    onChangeBorderColorTransparency,
    onChangeTextColorTransparency
}: TextLayerToolbarProps) => {
    const { t } = useTranslation('imageStudioToolbar');

    return(<>
        {
            windowSizeSmall
            ?   <EffectThemeContainer
                    containerClass="toolbar-effect-container mb-1 p-0 mt-0 rounded d-inline-block overflow-hidden"
                    children={
                        <HorizontalToolbarViewport>
                            <div
                                className="m-1 row align-items-start toolbar-main-row"
                                style={{
                                    width: "max-content",
                                    minWidth: "max-content",
                                }}
                            >
                                <div className="col-auto border-end border-secondary">
                                    <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                        <label className={ToolkitLabelStyle}>{t('label.text')}</label>
                                    </div>
                                    <div className="row">
                                        <div className="col-auto p-0 ps-1">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${textIsBold ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                title={`${textIsBold ? "Not bold text" : "Bold text"}`}
                                                onClick={() => setTextStyleIsBold?.(!textIsBold)}
                                                children={<FontAwesomeIcon icon={faBold} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0 ps-1">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${textIsItalic ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                title={`${textIsItalic ? "Not italic text" : "Italic text"}`}
                                                onClick={() => setTextStyleIsItalic?.(!textIsItalic)}
                                                children={<FontAwesomeIcon icon={faItalic} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0 ps-1">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${textIsUnderline ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                                title={`${textIsUnderline ? "Not underline text" : "Underline text"}`}
                                                onClick={() => setTextStyleIsUnderline?.(!textIsUnderline)}
                                                children={<FontAwesomeIcon icon={faUnderline} size="sm" className="p-1" />}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0 ps-1">
                                            <EffectThemeInput
                                                type="color"
                                                value={pickedTextColor}
                                                inputClassName="rounded-start-only p-1"
                                                title={t('text.chooseColor')}
                                                onValueChange={(value) => onSelectTextColor?.(String(value))}
                                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${isPickingTextColor ? "btn-primary" : "btn-outline-secondary"}`}
                                                title={t('text.pickColor')}
                                                onClick={(e) => {
                                                    onToggleTextColorPicker?.();
                                                }}
                                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0">
                                            <EffectThemeInput
                                                type="number"
                                                value={textOpacity}
                                                min={0}
                                                max={100}
                                                step={1}
                                                inputClassName="rounded-end-only p-1"
                                                title={t('text.changeOpacity')}
                                                onValueChange={(value) => onChangeTextColorTransparency?.(value)}
                                                style={{height: ToolkitItemsHeight,width: "60px" }}
                                            />
                                        </div>
                                        <div className="col-auto p-0 ps-1">
                                            <EffectThemeInput
                                                type="number"
                                                name="textSize"
                                                value={selectedTextSize}
                                                min={1}
                                                max={500}
                                                step={1}
                                                onValueChange={(value) => onSelectTextSize?.(Number(value))}
                                                inputClassName="p-1 rounded-start-only"
                                                style={{ height: ToolkitItemsHeight, width: "60px" }}
                                            />
                                        </div>
                                        <div className="col-auto p-0">
                                            <ToolbarPortalDropdown
                                                title={t('text.selectSize')}
                                                menuWidth={190}
                                                button={<FontAwesomeIcon icon={faTextHeight} size="sm" className="p-1" />}
                                                buttonClassname='rounded-end-only'
                                            >
                                                {textSizesList.map((item, index) => (
                                                    <ToolbarPortalDropdownItem
                                                        key={index}
                                                        active={item === selectedTextSize}
                                                        onClick={() => {
                                                            onSelectTextSize?.(item);
                                                        }}
                                                        className="p-1 ps-2"
                                                    >
                                                        {item} px
                                                    </ToolbarPortalDropdownItem>
                                                ))}
                                            </ToolbarPortalDropdown>
                                        </div>
                                        <div className="col-auto p-0 ps-1  pe-1">
                                            <ToolbarPortalDropdown
                                                title={t('text.selectFont')}
                                                menuWidth={190}
                                                button={<FontAwesomeIcon icon={faFont} size="sm" className="p-1" />}
                                            >
                                                {fontTypesList.map((item, index) => (
                                                    <ToolbarPortalDropdownItem
                                                        key={index}
                                                        active={item === selectedFontType}
                                                        onClick={() => {
                                                            onSelectFontType?.(item);
                                                        }}
                                                        className="p-1 ps-2"
                                                        style={{
                                                            fontFamily: item
                                                        }}
                                                    >
                                                        {item} px
                                                    </ToolbarPortalDropdownItem>
                                                ))}
                                            </ToolbarPortalDropdown>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-auto border-end border-secondary">
                                    <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                        <label className={ToolkitLabelStyle}>{t('label.border')}</label>
                                    </div>
                                    <div className="row">
                                        <div className="col-auto  p-0 ps-1">
                                            <EffectThemeInput
                                                type="color"
                                                value={pickedBorderColor}
                                                inputClassName="rounded-start-only p-1"
                                                title={t('border.chooseColor')}
                                                onValueChange={(value) => onSelectBorderColor?.(String(value))}
                                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto  p-0">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${isPickingBorderColor ? "btn-primary" : "btn-outline-secondary"}`}
                                                title={t('border.pickColor')}
                                                onClick={(e) => {
                                                    onToggleBorderColorPicker?.();
                                                }}
                                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0">
                                            <EffectThemeInput
                                                type="number"
                                                value={borderOpacity}
                                                min={0}
                                                max={100}
                                                step={1}
                                                inputClassName="rounded-end-only p-1"
                                                title={t('border.changeOpacityColor')}
                                                onValueChange={(value) => onChangeBorderColorTransparency?.(value)}
                                                style={{height: ToolkitItemsHeight,width: "60px" }}
                                            />
                                        </div>
                                        <div className="col-auto p-0 ps-1">
                                            <ToolbarPortalDropdown
                                                title={t('border.selectWidth')}
                                                menuWidth={190}
                                                button={<IconBorderWidth/>}
                                            >
                                                {borderWidthList.map((item, index) => (
                                                    <ToolbarPortalDropdownItem
                                                        key={index}
                                                        active={item === selectedBorderWidth}
                                                        onClick={() => {
                                                            onSelectBorderWidth?.(item);
                                                        }}
                                                        className="p-1 ps-2"
                                                    >
                                                        {item} px
                                                    </ToolbarPortalDropdownItem>
                                                ))}
                                            </ToolbarPortalDropdown>
                                        </div>
                                        <div className="col-auto p-0 ps-1 pe-1">
                                            <ToolbarPortalDropdown
                                                title={t('border.selectStyle')}
                                                menuWidth={190}
                                                button={<FontAwesomeIcon icon={faBorderNone} size="sm" className="p-1" />}
                                            >
                                                {borderStyleList.map((item, index) => (
                                                    <ToolbarPortalDropdownItem
                                                        key={index}
                                                        active={item === selectedBorderType}
                                                        onClick={() => {
                                                            onSelectBorderType?.(item);
                                                        }}
                                                        className="p-1 ps-2"
                                                    >
                                                        {item}
                                                    </ToolbarPortalDropdownItem>
                                                ))}
                                            </ToolbarPortalDropdown>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-auto">
                                    <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                        <label className={ToolkitLabelStyle}>{t('label.background')}</label>
                                    </div>
                                    <div className="row">
                                        <div className="col-auto  p-0 ps-1">
                                            <EffectThemeInput
                                                type="color"
                                                value={pickedBackgroundColor}
                                                inputClassName="rounded-start-only p-1"
                                                title={t('background.chooseColor')}
                                                onValueChange={(value) => onSelectBackgroundColor?.(String(value))}
                                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto  p-0">
                                            <EffectThemeTabButton
                                                className={`btn p-1 ${isPickingBackgroundColor ? "btn-primary" : "btn-outline-secondary"}`}
                                                title={t('background.pickColor')}
                                                onClick={(e) => {
                                                    onToggleBackgroundColorPicker?.();
                                                }}
                                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                        <div className="col-auto p-0 pe-1">
                                            <EffectThemeInput
                                                type="number"
                                                value={backgroundOpacity}
                                                min={0}
                                                max={100}
                                                step={1}
                                                inputClassName="rounded-end-only p-1"
                                                title={t('background.changeOpacityColor')}
                                                onValueChange={(value) => onChangeBackgroundColorTransparency?.(value)}
                                                style={{height: ToolkitItemsHeight, width: "60px" }}
                                            />
                                        </div>
                                        <div className="col-auto  p-0">
                                            <EffectThemeTabButton
                                                className={`btn p-1 rounded`}
                                                title={t('background.remove')}
                                                onClick={(e) => {onSelectBackgroundColor?.('transparent')}}
                                                children={<FontAwesomeIcon icon={faX} size="sm" className="p-1"/>}
                                                style={{height: ToolkitItemsHeight}}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </HorizontalToolbarViewport>
                    }
                />
            :   <EffectThemeContainer
                containerClass="p-1 px-2 rounded d-inline-block mb-1"
                children={
                    <div className="row p-1">
                        <div className="col-auto p-0 ps-1 me-1">
                            <label className="fw-bold">{t('label.text')}: </label>
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <EffectThemeTabButton
                                className={`btn p-1 ${textIsBold ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                title={`${textIsBold ? "Not bold text" : "Bold text"}`}
                                onClick={() => setTextStyleIsBold?.(!textIsBold)}
                                children={<FontAwesomeIcon icon={faBold} size="sm" className="p-1" />}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <EffectThemeTabButton
                                className={`btn p-1 ${textIsItalic ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                title={`${textIsItalic ? "Not italic text" : "Italic text"}`}
                                onClick={() => setTextStyleIsItalic?.(!textIsItalic)}
                                children={<FontAwesomeIcon icon={faItalic} size="sm" className="p-1" />}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <EffectThemeTabButton
                                className={`btn p-1 ${textIsUnderline ? "btn-primary" : "btn-outline-secondary"} rounded`}
                                title={`${textIsUnderline ? "Not underline text" : "Underline text"}`}
                                onClick={() => setTextStyleIsUnderline?.(!textIsUnderline)}
                                children={<FontAwesomeIcon icon={faUnderline} size="sm" className="p-1" />}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <EffectThemeInput
                                type="color"
                                value={pickedTextColor}
                                inputClassName="rounded-start-only p-1"
                                title={t('text.chooseColor')}
                                onValueChange={(value) => onSelectTextColor?.(String(value))}
                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0">
                            <EffectThemeTabButton
                                className={`btn p-1 ${isPickingTextColor ? "btn-primary" : "btn-outline-secondary"}`}
                                title={t('text.pickColor')}
                                onClick={(e) => {
                                    onToggleTextColorPicker?.();
                                }}
                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0">
                            <EffectThemeInput
                                type="number"
                                value={textOpacity}
                                min={0}
                                max={100}
                                step={1}
                                inputClassName="rounded-end-only p-1"
                                title={t('text.changeOpacity')}
                                onValueChange={(value) => onChangeTextColorTransparency?.(value)}
                                style={{height: ToolkitItemsHeight,width: "60px" }}
                            />
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <EffectThemeInput
                                type="number"
                                name="textSize"
                                value={selectedTextSize}
                                min={1}
                                max={500}
                                step={1}
                                onValueChange={(value) => onSelectTextSize?.(Number(value))}
                                inputClassName="p-1 rounded-start-only"
                                style={{ height: ToolkitItemsHeight, width: "60px" }}
                            />
                        </div>
                        <div className="col-auto p-0">
                            <ToolbarPortalDropdown
                                title={t('text.selectSize')}
                                menuWidth={190}
                                button={<FontAwesomeIcon icon={faTextHeight} size="sm" className="p-1" />}
                                buttonClassname='rounded-end-only'
                            >
                                {textSizesList.map((item, index) => (
                                    <ToolbarPortalDropdownItem
                                        key={index}
                                        active={item === selectedTextSize}
                                        onClick={() => {
                                            onSelectTextSize?.(item);
                                        }}
                                        className="p-1 ps-2"
                                    >
                                        {item} px
                                    </ToolbarPortalDropdownItem>
                                ))}
                            </ToolbarPortalDropdown>
                        </div>
                        <div className="col-auto p-0 ps-1  pe-2 border-end border-secondary">
                            <ToolbarPortalDropdown
                                title={t('text.selectFont')}
                                menuWidth={190}
                                button={<FontAwesomeIcon icon={faFont} size="sm" className="p-1" />}
                            >
                                {fontTypesList.map((item, index) => (
                                    <ToolbarPortalDropdownItem
                                        key={index}
                                        active={item === selectedFontType}
                                        onClick={() => {
                                            onSelectFontType?.(item);
                                        }}
                                        className="p-1 ps-2"
                                        style={{
                                            fontFamily: item
                                        }}
                                    >
                                        {item} px
                                    </ToolbarPortalDropdownItem>
                                ))}
                            </ToolbarPortalDropdown>
                        </div>
                        <div className="col-auto p-0 ps-2 me-1">
                            <label className="fw-bold">{t('label.border')}: </label>
                        </div>
                        <div className="col-auto  p-0 ps-1">
                            <EffectThemeInput
                                type="color"
                                value={pickedBorderColor}
                                inputClassName="rounded-start-only p-1"
                                title={t('border.chooseColor')}
                                onValueChange={(value) => onSelectBorderColor?.(String(value))}
                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto  p-0">
                            <EffectThemeTabButton
                                className={`btn p-1 ${isPickingBorderColor ? "btn-primary" : "btn-outline-secondary"}`}
                                title={t('border.pickColor')}
                                onClick={(e) => {
                                    onToggleBorderColorPicker?.();
                                }}
                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0">
                            <EffectThemeInput
                                type="number"
                                value={borderOpacity}
                                min={0}
                                max={100}
                                step={1}
                                inputClassName="rounded-end-only p-1"
                                title={t('border.changeOpacityColor')}
                                onValueChange={(value) => onChangeBorderColorTransparency?.(value)}
                                style={{height: ToolkitItemsHeight,width: "60px" }}
                            />
                        </div>
                        <div className="col-auto p-0 ps-1">
                            <ToolbarPortalDropdown
                                title={t('border.selectWidth')}
                                menuWidth={190}
                                button={<IconBorderWidth/>}
                            >
                                {borderWidthList.map((item, index) => (
                                    <ToolbarPortalDropdownItem
                                        key={index}
                                        active={item === selectedBorderWidth}
                                        onClick={() => {
                                            onSelectBorderWidth?.(item);
                                        }}
                                        className="p-1 ps-2"
                                    >
                                        {item} px
                                    </ToolbarPortalDropdownItem>
                                ))}
                            </ToolbarPortalDropdown>
                        </div>
                        <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary">
                            <ToolbarPortalDropdown
                                title={t('border.selectStyle')}
                                menuWidth={190}
                                button={<FontAwesomeIcon icon={faBorderNone} size="sm" className="p-1" />}
                            >
                                {borderStyleList.map((item, index) => (
                                    <ToolbarPortalDropdownItem
                                        key={index}
                                        active={item === selectedBorderType}
                                        onClick={() => {
                                            onSelectBorderType?.(item);
                                        }}
                                        className="p-1 ps-2"
                                    >
                                        {item}
                                    </ToolbarPortalDropdownItem>
                                ))}
                            </ToolbarPortalDropdown>
                        </div>
                        <div className="col-auto p-0 ps-2 me-1">
                            <label className="fw-bold">{t('label.background')}: </label>
                        </div>
                        <div className="col-auto  p-0 ps-1">
                            <EffectThemeInput
                                type="color"
                                value={pickedBackgroundColor}
                                inputClassName="rounded-start-only p-1"
                                title={t('background.chooseColor')}
                                onValueChange={(value) => onSelectBackgroundColor?.(String(value))}
                                style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto  p-0">
                            <EffectThemeTabButton
                                className={`btn p-1 ${isPickingBackgroundColor ? "btn-primary" : "btn-outline-secondary"}`}
                                title={t('background.pickColor')}
                                onClick={(e) => {
                                    onToggleBackgroundColorPicker?.();
                                }}
                                children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                style={{height: ToolkitItemsHeight}}
                            />
                        </div>
                        <div className="col-auto p-0 pe-2">
                            <EffectThemeInput
                                type="number"
                                value={backgroundOpacity}
                                min={0}
                                max={100}
                                step={1}
                                inputClassName="rounded-end-only p-1"
                                title={t('background.changeOpacityColor')}
                                onValueChange={(value) => onChangeBackgroundColorTransparency?.(value)}
                                style={{height: ToolkitItemsHeight, width: "60px" }}
                            />
                        </div>
                    </div>
                }
            />
        }
    </>);
};

export const ImageLayerToolbar = ({
    image,
    imageOpacity=100,
    backgroundColor='#ffff',
    backgroundOpacity=100,
    borderColor='#ffff',
    borderOpacity=100,
    borderStyleList=[],
    borderStyle='none',
    borderWidth=1,
    borderWidthList=[],
    isPickingBackgroundColor=false,
    isPickingBorderColor=false,
    windowSizeSmall=false,
    onChangeBackgroundColorTransparency,
    onChangeBorderColorTransparency,
    onChangeImage,
    onChangeImageOpacity,
    onSelectBackgroundColor,
    onSelectBorderColor,
    onSelectBorderType,
    onSelectBorderWidth,
    onToggleBackgroundColorPicker,
    onToggleBorderColorPicker
}: ImageLayerToolkitProps) => {
    const { t } = useTranslation('imageStudioToolbar');
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleButtonClick = () => {
        fileInputRef.current?.click();
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        let imageDimensions = await getImageDimensions(file);

        // hier kannst du ggf. noch validieren (type, size, etc.)

        onChangeImage?.({
            file: file,
            src: URL.createObjectURL(file),
            timeOfUse: new Date(),
            width:imageDimensions.width,
            height: imageDimensions.height 
        });


        // wichtig: reset damit gleiche Datei nochmal gewählt werden kann
        e.target.value = "";
    };
    return(
        <>
            {
                windowSizeSmall
                ?   <EffectThemeContainer
                        containerClass="toolbar-effect-container mb-1 p-0 mt-0 rounded d-inline-block overflow-hidden"
                        children={
                            <HorizontalToolbarViewport>
                                <div
                                    className="m-1 row align-items-start toolbar-main-row"
                                    style={{
                                        width: "max-content",
                                        minWidth: "max-content",
                                        zIndex: 40000
                                    }}
                                >
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.image')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <input
                                                    ref={fileInputRef}
                                                    type="file"
                                                    accept="image/*"
                                                    style={{ display: "none" }}
                                                    onChange={handleFileChange}
                                                    onClick={() => fileInputRef.current?.click()}
                                                />

                                                <EffectThemeTabButton
                                                    className={`btn p-1 btn-outline-secondary rounded`}
                                                    title={image ? t('image.replaceTitle') : t('image.uploadTitle')}
                                                    onClick={handleButtonClick}
                                                    style={{ height: ToolkitItemsHeight }}
                                                >
                                                    <FontAwesomeIcon icon={faUpload} size="sm" className="p-1" />
                                                </EffectThemeTabButton>
                                            </div>
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <EffectThemeInput
                                                    type="number"
                                                    value={imageOpacity}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    inputClassName="rounded p-1"
                                                    title={t('image.changeOpacity')}
                                                    onValueChange={(value) => onChangeImageOpacity?.(value)}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto border-end border-secondary">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.border')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto p-0 ps-1">
                                                <EffectThemeInput
                                                    type="color"
                                                    value={borderColor}
                                                    inputClassName="rounded-start-only p-1"
                                                    title={t('border.chooseColor')}
                                                    onValueChange={(value) => onSelectBorderColor?.(String(value))}
                                                    style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${isPickingBorderColor ? "btn-primary" : "btn-outline-secondary"}`}
                                                    title={t('border.pickColor')}
                                                    onClick={(e) => {
                                                        onToggleBorderColorPicker?.();
                                                    }}
                                                    children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0">
                                                <EffectThemeInput
                                                    type="number"
                                                    value={borderOpacity}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    inputClassName="rounded-end-only p-1"
                                                    title={t('border.changeOpacity')}
                                                    onValueChange={(value) => onChangeBorderColorTransparency?.(value)}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 ps-1">
                                                <ToolbarPortalDropdown
                                                    title={t('border.selectWidth')}
                                                    menuWidth={190}
                                                    button={<IconBorderWidth/>}
                                                >
                                                    {borderWidthList.map((item, index) => (
                                                        <ToolbarPortalDropdownItem
                                                            key={index}
                                                            active={item === borderWidth}
                                                            onClick={() => {
                                                                onSelectBorderWidth?.(item);
                                                            }}
                                                            className="p-1 ps-2"
                                                        >
                                                            {item} px
                                                        </ToolbarPortalDropdownItem>
                                                    ))}
                                                </ToolbarPortalDropdown>
                                            </div>
                                            <div className="col-auto p-0 ps-1 pe-1">
                                                <ToolbarPortalDropdown
                                                    title={t('border.selectStyle')}
                                                    menuWidth={190}
                                                    button={<FontAwesomeIcon icon={faBorderNone} size="sm" className="p-1" />}
                                                >
                                                    {borderStyleList.map((item, index) => (
                                                        <ToolbarPortalDropdownItem
                                                            key={index}
                                                            active={item === borderStyle}
                                                            onClick={() => {
                                                                onSelectBorderType?.(item);
                                                            }}
                                                            className="p-1 ps-2"
                                                        >
                                                            {item}
                                                        </ToolbarPortalDropdownItem>
                                                    ))}
                                                </ToolbarPortalDropdown>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-auto">
                                        <div className={`row text-${ToolkitLabelPosition} mb-1`}>
                                            <label className={ToolkitLabelStyle}>{t('label.background')}</label>
                                        </div>
                                        <div className="row">
                                            <div className="col-auto  p-0 ps-1">
                                                <EffectThemeInput
                                                    type="color"
                                                    value={backgroundColor}
                                                    inputClassName="rounded-start-only p-1"
                                                    title={t('background.chooseColor')}
                                                    onValueChange={(value) => onSelectBackgroundColor?.(String(value))}
                                                    style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto  p-0">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 ${isPickingBackgroundColor ? "btn-primary" : "btn-outline-secondary"}`}
                                                    title={t('background.pickColor')}
                                                    onClick={(e) => {
                                                        onToggleBackgroundColorPicker?.();
                                                    }}
                                                    children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto p-0 pe-1">
                                                <EffectThemeInput
                                                    type="number"
                                                    value={backgroundOpacity}
                                                    min={0}
                                                    max={100}
                                                    step={1}
                                                    inputClassName="rounded-end-only p-1"
                                                    title={t('background.changeOpacity')}
                                                    onValueChange={(value) => onChangeBackgroundColorTransparency?.(value)}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                            <div className="col-auto  p-0">
                                                <EffectThemeTabButton
                                                    className={`btn p-1 rounded`}
                                                    title={t('background.remove')}
                                                    onClick={(e) => {onSelectBackgroundColor?.('transparent')}}
                                                    children={<FontAwesomeIcon icon={faX} size="sm" className="p-1"/>}
                                                    style={{height: ToolkitItemsHeight}}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </HorizontalToolbarViewport>
                        }
                    />
                : <EffectThemeContainer
                    containerClass="p-1 px-2 rounded d-inline-block mb-1"
                    children={
                        <div className="row p-1">
                            <div className="col-auto p-0 ps-1 me-1">
                                <label className="fw-bold">{t('label.image')}: </label>
                            </div>
                            
                            <div className="col-auto p-0 ps-1">
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/*"
                                    style={{ display: "none" }}
                                    onChange={handleFileChange}
                                    onClick={() => fileInputRef.current?.click()}
                                />

                                <EffectThemeTabButton
                                    className={`btn p-1 btn-outline-secondary rounded`}
                                    title={image ? t('image.replaceTitle') : t('image.uploadTitle')}
                                    onClick={handleButtonClick}
                                    style={{ height: ToolkitItemsHeight }}
                                >
                                    <FontAwesomeIcon icon={faUpload} size="sm" className="p-1" />
                                </EffectThemeTabButton>
                            </div>
                            <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary">
                                <EffectThemeInput
                                    type="number"
                                    value={imageOpacity}
                                    min={0}
                                    max={100}
                                    step={1}
                                    inputClassName="rounded p-1"
                                    title={t('image.changeOpacity')}
                                    onValueChange={(value) => onChangeImageOpacity?.(value)}
                                    style={{height: ToolkitItemsHeight}}
                                />
                            </div>

                            <div className="col-auto p-0 ps-2 me-1">
                                <label className="fw-bold">{t('label.border')}: </label>
                            </div>
                            <div className="col-auto  p-0 ps-1">
                                    <EffectThemeInput
                                        type="color"
                                        value={borderColor}
                                        inputClassName="rounded-start-only p-1"
                                        title={t('border.chooseColor')}
                                        onValueChange={(value) => onSelectBorderColor?.(String(value))}
                                        style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                    />
                            </div>
                            <div className="col-auto  p-0">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${isPickingBorderColor ? "btn-primary" : "btn-outline-secondary"}`}
                                        title={t('border.pickColor')}
                                        onClick={(e) => {
                                            onToggleBorderColorPicker?.();
                                        }}
                                        children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                            </div>
                            <div className="col-auto p-0">
                                <EffectThemeInput
                                    type="number"
                                    value={borderOpacity}
                                    min={0}
                                    max={100}
                                    step={1}
                                    inputClassName="rounded-end-only p-1"
                                    title={t('border.changeOpacity')}
                                    onValueChange={(value) => onChangeBorderColorTransparency?.(value)}
                                    style={{height: ToolkitItemsHeight}}
                                />
                            </div>
                            <div className="col-auto p-0 ps-1">
                                <ToolbarPortalDropdown
                                    title={t('border.selectWidth')}
                                    menuWidth={190}
                                    button={<IconBorderWidth/>}
                                >
                                    {borderWidthList.map((item, index) => (
                                        <ToolbarPortalDropdownItem
                                            key={index}
                                            active={item === borderWidth}
                                            onClick={() => {
                                                onSelectBorderWidth?.(item);
                                            }}
                                            className="p-1 ps-2"
                                        >
                                            {item} px
                                        </ToolbarPortalDropdownItem>
                                    ))}
                                </ToolbarPortalDropdown>
                            </div>
                            <div className="col-auto p-0 ps-1 pe-2 border-end border-secondary">
                                <ToolbarPortalDropdown
                                    title={t('border.selectStyle')}
                                    menuWidth={190}
                                    button={<FontAwesomeIcon icon={faBorderNone} size="sm" className="p-1" />}
                                >
                                    {borderStyleList.map((item, index) => (
                                        <ToolbarPortalDropdownItem
                                            key={index}
                                            active={item === borderStyle}
                                            onClick={() => {
                                                onSelectBorderType?.(item);
                                            }}
                                            className="p-1 ps-2"
                                        >
                                            {item}
                                        </ToolbarPortalDropdownItem>
                                    ))}
                                </ToolbarPortalDropdown>
                            </div>
                            <div className="col-auto p-0 ps-2 me-1">
                                <label className="fw-bold">{t('label.background')}: </label>
                            </div>
                            <div className="col-auto  p-0 ps-1">
                                        <EffectThemeInput
                                            type="color"
                                            value={backgroundColor}
                                            inputClassName="rounded-start-only p-1"
                                            title={t('background.chooseColor')}
                                            onValueChange={(value) => onSelectBackgroundColor?.(String(value))}
                                            style={{height: ToolkitItemsHeight, width: ToolkitItemsHeight}}
                                        />
                            </div>
                            <div className="col-auto  p-0">
                                    <EffectThemeTabButton
                                        className={`btn p-1 ${isPickingBackgroundColor ? "btn-primary" : "btn-outline-secondary"}`}
                                        title={t('background.pickColor')}
                                        onClick={(e) => {
                                            onToggleBackgroundColorPicker?.();
                                        }}
                                        children={<FontAwesomeIcon icon={faEyeDropper} size="sm" className="p-1"/>}
                                        style={{height: ToolkitItemsHeight}}
                                    />
                            </div>
                            <div className="col-auto p-0 pe-2">
                                <EffectThemeInput
                                    type="number"
                                    value={backgroundOpacity}
                                    min={0}
                                    max={100}
                                    step={1}
                                    inputClassName="rounded-end-only p-1"
                                    title={t('background.changeOpacity')}
                                    onValueChange={(value) => onChangeBackgroundColorTransparency?.(value)}
                                    style={{height: ToolkitItemsHeight}}
                                />
                            </div>
                        </div>
                    }
                />
            }
        </>
    );
};